import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";

import GuestLayout from "@/components/guest/GuestLayout.jsx";
import WelcomeCard from "@/components/guest/WelcomeCard.jsx";
import RequestStatusBadge from "@/components/guest/RequestStatusBadge.jsx";
import GuestDepartmentCallPanel from "@/components/guest/GuestDepartmentCallPanel.jsx";

import { useSession } from "@/hooks/useSession.js";
import { recommendationApi } from "@/api/guest/recommendationApi";
import { guestRequestsApi } from "@/api/guestRequests/guestRequestapi.js";
import { guestsApi } from "@/api/guests/guestsApi.js";

import {
  Sparkles,
  UtensilsCrossed,
  Shirt,
  MapPin,
  HelpCircle,
  Loader2,
  ConciergeBell,
  ClipboardCheck,
  Clock,
  Tag,
  Bell,
  BellRing,
  Eye,
  Receipt,
  UserPlus,
  UserRound,
  Gift,
  Crown,
  Ticket,
  LayoutGrid,
  List,
  ChevronRight,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { getApiData, getApiMessage, getPagedItems } from "@/utils/apiHelpers.js";
import { Input } from "@/components/ui/input";

const formatResolutionTime = (request) => {
  const formatMins = (m) => {
    if (!m || m <= 0) return null;
    if (m >= 1440) {
      const days = Math.floor(m / 1440);
      const hours = Math.floor((m % 1440) / 60);
      return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
    }
    if (m >= 60) {
      const hours = Math.floor(m / 60);
      const mins = m % 60;
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    }
    return `${m} min`;
  };

  if (request.resolutionMinutes) {
    const formatted = formatMins(request.resolutionMinutes);
    return formatted ? `Completed in ${formatted}` : "Completed";
  }

  if (request.overdueMinutes) {
    const formatted = formatMins(request.overdueMinutes);
    return formatted ? `${formatted} overdue` : "Overdue";
  }

  if (request.benchmarkMinutes) {
    const formatted = formatMins(request.benchmarkMinutes);
    return formatted ? `${formatted} target` : "In progress";
  }

  return "Tracking live status";
};

const fallbackActivityImage =
  "https://images.unsplash.com/photo-1551218808-94e220e084d2?auto=format&fit=crop&w=1200&q=80";

const normalizeRecommendation = (item) => {
  return {
    id: item.id || item.Id,
    name: item.name || item.Name || "Recommended Activity",
    category: item.category || item.Category || "Recommendation",
    price:
      item.price || item.Price || item.price === 0 || item.Price === 0
        ? `Rs ${Number(item.price ?? item.Price ?? 0).toFixed(2)}`
        : "Contact hotel",
    duration: item.duration || item.Duration || "Flexible",
    description:
      item.shortDescription ||
      item.ShortDescription ||
      item.description ||
      item.Description ||
      "Recommended experience from the hotel.",
    image:
      item.imageUrl ||
      item.ImageUrl ||
      item.image ||
      item.Image ||
      fallbackActivityImage,
  };
};

const normalizeGuestRequest = (item) => {
  const type =
    item?.requestTypeName ||
    item?.RequestTypeName ||
    item?.requestType ||
    item?.RequestType;
  const sourceType = item?.sourceType || item?.SourceType || "";
  const sourceId = item?.sourceId || item?.SourceId || null;
  const orderId = item?.orderId || item?.OrderId || (
    ["order", "posorder"].includes(String(sourceType).toLowerCase()) ? sourceId : null
  );

  return {
    id: item?.id || item?.Id,
    sourceType,
    sourceId,
    orderId,
    title:
      item?.title ||
      item?.Title ||
      String(type || "Service request").replace(/([a-z])([A-Z])/g, "$1 $2"),
    status:
      item?.statusName ||
      item?.StatusName ||
      item?.status ||
      item?.Status ||
      "Pending",
    createdAt: item?.createdAtUtc || item?.CreatedAtUtc,
    benchmarkMinutes: item?.benchmarkMinutes || item?.BenchmarkMinutes,
    resolutionMinutes: item?.resolutionMinutes || item?.ResolutionMinutes,
    overdueMinutes: item?.overdueMinutes || item?.OverdueMinutes,
    isOverdue: item?.isOverdue ?? item?.IsOverdue ?? false,
    reminderCount: item?.reminderCount || item?.ReminderCount || 0,
  };
};

const canPingRequest = (status = "") => {
  const value = String(status).toLowerCase();
  return !["completed", "cancelled", "canceled", "rejected"].includes(value);
};

const GlassActionCard = ({
  icon: Icon,
  title,
  description,
  onClick,
  gradient = "from-[#ff3b30] via-[#ff2d55] to-[#e0284f]",
  className = "",
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        group flex min-h-[72px] w-full items-center justify-start gap-3.5
        rounded-2xl border border-white/70
        bg-white/95 px-4 py-3 text-left
        backdrop-blur-xl transition-all duration-200
        hover:bg-white active:scale-[0.98]
        shadow-[0_14px_36px_rgba(15,23,42,0.12)]
        ${className}
      `}
    >
      <div
        className={`
          relative flex h-11 w-11 shrink-0 items-center justify-center
          rounded-2xl bg-gradient-to-br ${gradient} text-white
          shadow-md ring-1 ring-white/30 transition-transform duration-200 group-hover:scale-105
        `}
      >
        <div className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-b from-white/35 via-white/10 to-transparent" />
        <Icon className="relative z-10 h-5 w-5 drop-shadow-sm" />
      </div>

      <div className="min-w-0 flex-1">
        <h3 className="text-xs font-bold leading-tight text-slate-900 sm:text-sm">
          {title}
        </h3>
        {description && (
          <p className="mt-0.5 text-[11px] leading-normal text-slate-500 line-clamp-1">
            {description}
          </p>
        )}
      </div>

      <ChevronRight className="h-4 w-4 shrink-0 text-slate-400 transition-transform group-hover:translate-x-0.5" />
    </button>
  );
};

const IOSQuickActionsGrid = ({ actions, navigate }) => {
  const [viewMode, setViewMode] = useState("grid");

  return (
    <div className="space-y-3">
      {/* Header with iOS Segmented Control */}
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-lg font-extrabold text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.5)] sm:text-xl">
          Quick Actions
        </h2>

        {/* iOS Segmented Control */}
        <div className="flex items-center rounded-2xl border border-white/30 bg-black/40 p-1 backdrop-blur-xl shadow-lg">
          <button
            type="button"
            onClick={() => setViewMode("grid")}
            className={`flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-xl transition-all duration-200 ${
              viewMode === "grid"
                ? "bg-white text-slate-900 shadow-md scale-100"
                : "text-white/80 hover:text-white hover:bg-white/10"
            }`}
          >
            <LayoutGrid className="h-3.5 w-3.5" />
            App Grid
          </button>
          <button
            type="button"
            onClick={() => setViewMode("tiles")}
            className={`flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-xl transition-all duration-200 ${
              viewMode === "tiles"
                ? "bg-white text-slate-900 shadow-md scale-100"
                : "text-white/80 hover:text-white hover:bg-white/10"
            }`}
          >
            <List className="h-3.5 w-3.5" />
            Tiles
          </button>
        </div>
      </div>

      {viewMode === "grid" ? (
        /* iOS App Icon Grid Container */
        <div className="rounded-3xl border border-white/35 bg-slate-900/40 p-4 shadow-[0_24px_54px_rgba(0,0,0,0.3)] backdrop-blur-2xl sm:p-6">
          <div className="grid grid-cols-4 gap-y-5 gap-x-2 sm:grid-cols-5 sm:gap-5 md:grid-cols-5">
            {actions.map((action) => {
              const Icon = action.icon;
              return (
                <div
                  key={action.title}
                  className="flex flex-col items-center justify-start text-center"
                >
                  {/* Native iOS App Squircle Button */}
                  <button
                    type="button"
                    onClick={() => (action.onClick ? action.onClick() : navigate(action.to))}
                    aria-label={action.fullTitle || action.title}
                    className={`
                      relative group flex h-14 w-14 items-center justify-center sm:h-16 sm:w-16
                      rounded-[18px] sm:rounded-[22px]
                      bg-gradient-to-br ${action.gradient || "from-slate-700 via-slate-800 to-slate-900"}
                      text-white ${action.shadow || "shadow-black/30"} shadow-lg
                      ring-1 ring-white/40 ring-inset
                      transition-all duration-200 ease-out
                      active:scale-90 hover:scale-105 hover:shadow-2xl focus:outline-none
                    `}
                  >
                    {/* Top glossy sheen overlay */}
                    <div className="pointer-events-none absolute inset-0 rounded-[18px] sm:rounded-[22px] bg-gradient-to-b from-white/35 via-white/10 to-transparent" />

                    {/* Icon */}
                    <Icon className="relative z-10 h-6 w-6 sm:h-7 sm:w-7 drop-shadow-[0_2px_4px_rgba(0,0,0,0.4)]" />
                  </button>

                  {/* App Label beneath - 100% visible, clean two-line wrap */}
                  <span className="mt-1.5 text-[11px] font-semibold leading-tight text-white drop-shadow-[0_1.5px_4px_rgba(0,0,0,0.9)] max-w-[76px] sm:max-w-[84px] line-clamp-2 px-0.5 sm:text-xs">
                    {action.title}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        /* iOS Grouped Control Inset Tiles View */
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
          {actions.map((action) => (
            <GlassActionCard
              key={action.title}
              icon={action.icon}
              title={action.fullTitle || action.title}
              description={action.description}
              gradient={action.gradient}
              onClick={() => (action.onClick ? action.onClick() : navigate(action.to))}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const RecommendedActivitiesSlider = ({ activities, onBook, bookingId }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (!activities.length) return;

    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % activities.length);
    }, 2000);

    return () => clearInterval(interval);
  }, [activities.length]);

  const activeActivity = activities[activeIndex];

  if (!activeActivity) return null;

  return (
    <div
      className="
        relative overflow-hidden rounded-3xl
        border border-white/70
        bg-white/90
        backdrop-blur-xl
        shadow-[0_22px_52px_rgba(15,23,42,0.18)]
      "
    >
      <div
        className="relative h-44 bg-cover bg-center transition-all duration-700 sm:h-72"
        style={{ backgroundImage: `url(${activeActivity.image})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-black/5" />

        <div className="absolute left-4 top-4 rounded-full border border-white/25 bg-black/40 px-4 py-1.5 text-sm font-semibold text-white backdrop-blur-md shadow-none">
          {activeActivity.category}
        </div>
      </div>

      <div className="min-h-[160px] space-y-3 bg-white/95 p-4 sm:min-h-[210px] sm:space-y-4 sm:p-6">
        <div className="flex min-h-[86px] items-start justify-between gap-3 sm:min-h-[110px] sm:gap-4">
          <div className="min-w-0">
            <h3 className="line-clamp-1 text-lg font-bold text-slate-800 sm:text-2xl">
              {activeActivity.name}
            </h3>

            <p className="mt-1.5 line-clamp-2 text-sm leading-relaxed text-slate-600 sm:mt-2 sm:line-clamp-3 sm:text-base">
              {activeActivity.description}
            </p>
          </div>

          <div className="shrink-0 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-right shadow-sm sm:px-4 sm:py-3">
            <p className="text-xs font-medium text-slate-500">Price</p>
            <p className="text-lg font-bold text-slate-800">
              {activeActivity.price}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 text-sm text-slate-700">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1">
            <Clock className="h-4 w-4" />
            {activeActivity.duration}
          </span>

          <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1">
            <Tag className="h-4 w-4" />
            Limited offer
          </span>
        </div>

        <div className="flex items-center justify-center gap-2 pt-1">
          {activities.map((activity, index) => (
            <Button
              key={activity.id}
              type="button"
              onClick={() => setActiveIndex(index)}
              className={`h-2 rounded-full transition-all duration-300 shadow-none ${
                activeIndex === index
                  ? "w-8 bg-slate-800"
                  : "w-2 bg-slate-400/50 hover:bg-slate-500"
              }`}
              aria-label={`Show ${activity.name}`}
            />
          ))}
        </div>

        <Button
          type="button"
          onClick={() => onBook?.(activeActivity)}
          disabled={bookingId === activeActivity.id}
          className="w-full bg-[#923536] text-white hover:bg-[#7f2f30]"
        >
          {bookingId === activeActivity.id ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : null}
          Book activity
        </Button>
      </div>
    </div>
  );
};

const GuestHomePage = () => {
  const navigate = useNavigate();

  const {
    session,
    hotel,
    room,
    loading: sessionLoading,
  } = useSession();

  const [recommendedActivities, setRecommendedActivities] = useState([]);
  const [recommendationsLoading, setRecommendationsLoading] = useState(false);
  const [bookingId, setBookingId] = useState(null);
  const [guestRequests, setGuestRequests] = useState([]);
  const [guestRequestsLoading, setGuestRequestsLoading] = useState(false);
  const [pingingRequestId, setPingingRequestId] = useState(null);
  const [registrationOpen, setRegistrationOpen] = useState(false);
  const [registrationSaving, setRegistrationSaving] = useState(false);
  const [registrationForm, setRegistrationForm] = useState({
    fullName: "",
    email: "",
    phoneNumber: "",
  });
  const guestSessionId = session?.id || session?.Id || session?.sessionId || session?.guestSessionId;

  useEffect(() => {
    if (!sessionLoading && !session) {
      navigate("/guest/expired", { replace: true });
    }
  }, [sessionLoading, session, navigate]);

  useEffect(() => {
    const loadRecommendations = async () => {
      try {
        setRecommendationsLoading(true);

        const response = await recommendationApi.getAll({
          pageNumber: 1,
          pageSize: 10,
          isAscending: false,
          sortBy: "createdAt",
        });

        const items = getPagedItems(response);

        setRecommendedActivities(
          Array.isArray(items) ? items.map(normalizeRecommendation) : []
        );
      } catch {
        setRecommendedActivities([]);
      } finally {
        setRecommendationsLoading(false);
      }
    };

    if (session) {
      loadRecommendations();
    }
  }, [session]);

  useEffect(() => {
    const loadGuestRequests = async () => {
      const sessionId = guestSessionId;
      if (!sessionId) return;

      try {
        setGuestRequestsLoading(true);
        const response = await guestRequestsApi.guestGetBySessionId(sessionId, {
          pageSize: 20,
        });
        const items = getPagedItems(response);
        setGuestRequests(
          Array.isArray(items) ? items.map(normalizeGuestRequest) : []
        );
      } catch (error) {
        toast.error(getApiMessage(error, "Failed to load your requests."));
        setGuestRequests([]);
      } finally {
        setGuestRequestsLoading(false);
      }
    };

    if (session) {
      loadGuestRequests();
    }
  }, [guestSessionId, session]);

  const handlePingRequest = async (requestId) => {
    try {
      setPingingRequestId(requestId);
      const response = await guestRequestsApi.remind(requestId, {
        note: "Guest requested a faster follow-up.",
        isInternal: false,
        guestSessionId,
      });
      const updatedRequest = normalizeGuestRequest(getApiData(response));

      setGuestRequests((current) =>
        current.map((request) =>
          request.id === requestId ? { ...request, ...updatedRequest } : request
        )
      );
      toast.success("Reminder sent to the hotel team");
    } catch (error) {
      toast.error(getApiMessage(error, "Could not send reminder."));
    } finally {
      setPingingRequestId(null);
    }
  };

  const handleGuestRegistration = async () => {
    if (!registrationForm.email.trim() && !registrationForm.phoneNumber.trim()) {
      toast.error("Enter an email or phone number to register.");
      return;
    }

    try {
      setRegistrationSaving(true);
      await guestsApi.registerFromGuestPortal({
        guestSessionId,
        fullName: registrationForm.fullName.trim() || session?.guestName || session?.GuestName || "Guest",
        email: registrationForm.email.trim(),
        phoneNumber: registrationForm.phoneNumber.trim(),
        sendVerificationEmail: Boolean(registrationForm.email.trim()),
      });
      toast.success("Registration started successfully.");
      setRegistrationOpen(false);
    } catch (error) {
      toast.error(getApiMessage(error, "Unable to start registration."));
    } finally {
      setRegistrationSaving(false);
    }
  };

  const handleBookActivity = async (activity) => {
    try {
      setBookingId(activity.id);
      const response = await guestRequestsApi.create({
        requestType: "Other",
        guestSessionId,
        title: `Activity booking: ${activity.name}`,
        description: [
          activity.description,
          activity.duration ? `Duration: ${activity.duration}` : "",
          activity.price ? `Price: ${activity.price}` : "",
        ].filter(Boolean).join("\n"),
      });
      const data = getApiData(response);
      toast.success("Booking request sent to the hotel");
      navigate("/guest/confirmation", { state: { request: data } });
    } catch (error) {
      toast.error(getApiMessage(error, "Failed to create booking request."));
    } finally {
      setBookingId(null);
    }
  };

  const quickActions = useMemo(
    () => [
      {
        icon: Sparkles,
        title: "Housekeeping",
        fullTitle: "Housekeeping Service",
        description: "Request room cleaning or turndown",
        to: "/guest/services",
        gradient: "from-[#ff2d55] via-[#ff3b30] to-[#e0284f]",
        shadow: "shadow-[#ff3b30]/35",
      },
      {
        icon: UtensilsCrossed,
        title: "In-Room Dining",
        fullTitle: "In-Room Dining",
        description: "Browse menu & order meals",
        to: "/guest/food-order",
        gradient: "from-[#ff9500] via-[#ff9f0a] to-[#d67e00]",
        shadow: "shadow-[#ff9f0a]/35",
      },
      {
        icon: Receipt,
        title: "My Bill",
        fullTitle: "My Bill & Charges",
        description: "Review estimated or final bill",
        to: "/guest/billing",
        gradient: "from-[#34c759] via-[#30d158] to-[#248a3d]",
        shadow: "shadow-[#30d158]/35",
      },
      {
        icon: UserRound,
        title: "My Profile",
        fullTitle: "Guest Profile",
        description: "Stay details & profile",
        to: "/guest/profile",
        gradient: "from-[#007aff] via-[#0a84ff] to-[#0056b3]",
        shadow: "shadow-[#0a84ff]/35",
      },
      {
        icon: Gift,
        title: "Loyalty Points",
        fullTitle: "Loyalty Points & Rewards",
        description: "Track points, tier & rewards",
        to: "/guest/loyalty",
        gradient: "from-[#ffcc00] via-[#ffd60a] to-[#cca300]",
        shadow: "shadow-[#ffd60a]/35",
      },
      {
        icon: Crown,
        title: "Memberships",
        fullTitle: "Active Memberships",
        description: "Hotel packages & benefits",
        to: "/guest/membership",
        gradient: "from-[#af52de] via-[#bf5af2] to-[#8934b8]",
        shadow: "shadow-[#bf5af2]/35",
      },
      {
        icon: Ticket,
        title: "My Coupons",
        fullTitle: "Event Coupons",
        description: "Package & event vouchers",
        to: "/guest/coupons",
        gradient: "from-[#ff6b00] via-[#ff5100] to-[#cc4100]",
        shadow: "shadow-[#ff5100]/35",
      },
      {
        icon: UserPlus,
        title: "Join Rewards",
        fullTitle: "Join Rewards Program",
        description: "Register for future stays",
        to: "/guest/register",
        gradient: "from-[#5ac8fa] via-[#64d2ff] to-[#2b9fd9]",
        shadow: "shadow-[#64d2ff]/35",
      },
      {
        icon: Shirt,
        title: "Laundry",
        fullTitle: "Laundry & Pressing",
        description: "Garment care & pressing",
        to: "/guest/services",
        gradient: "from-[#5856d6] via-[#5e5ce6] to-[#403eb5]",
        shadow: "shadow-[#5e5ce6]/35",
      },
      {
        icon: Bell,
        title: "Amenities",
        fullTitle: "Room Amenities",
        description: "Towels, toiletries & supplies",
        to: "/guest/services",
        gradient: "from-[#ff2d55] via-[#ff375f] to-[#cc1439]",
        shadow: "shadow-[#ff375f]/35",
      },
    ],
    []
  );

  if (sessionLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (!session) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>{`Welcome - ${hotel?.name || "The Hotelier"}`}</title>
        <meta
          name="description"
          content="Access hotel services and explore local attractions"
        />
      </Helmet>

      <GuestLayout hotel={hotel} room={room} session={session}>
        <div className="mx-auto max-w-4xl space-y-4 sm:space-y-6">
          <div className="overflow-hidden rounded-3xl border border-white/60 bg-white/90 shadow-[0_22px_52px_rgba(15,23,42,0.18)] backdrop-blur-xl">
            <WelcomeCard
              hotel={hotel}
              room={room}
              guestName={session.guestName || "Guest"}
            />
          </div>

          <section>
            <GlassActionCard
              icon={ConciergeBell}
              title="Hotel Services & Directory"
              description="View hotel amenities, timings, dining options, and guest guide"
              gradient="from-indigo-600 to-violet-700"
              onClick={() => navigate("/guest/hotel-services")}
            />
          </section>

          <GuestDepartmentCallPanel guestSessionId={guestSessionId} />

          <section>
            <div className="mb-2.5 flex items-center justify-between">
              <h2 className="text-lg font-extrabold text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.45)] sm:text-xl">
                My Requests
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/guest/services")}
                className="h-8 px-3 text-xs font-semibold text-white/95 drop-shadow-[0_2px_10px_rgba(0,0,0,0.35)] transition-all duration-200 hover:bg-white/20 hover:text-white"
              >
                + New request
              </Button>
            </div>

            <div className="rounded-3xl border border-white/70 bg-white/90 p-3.5 backdrop-blur-xl shadow-lg sm:p-4">
              {guestRequestsLoading ? (
                <div className="flex justify-center py-6">
                  <Loader2 className="h-7 w-7 animate-spin text-[#923536]" />
                </div>
              ) : guestRequests.length === 0 ? (
                <p className="py-3 text-center text-xs text-slate-500 sm:text-sm">
                  You have no active service requests right now.
                </p>
              ) : (
                <div className="space-y-2.5">
                  {guestRequests.slice(0, 5).map((request) => (
                    <div
                      key={request.id}
                      onClick={() => navigate(`/guest/status/${request.id}`)}
                      className="group relative cursor-pointer overflow-hidden rounded-2xl border border-slate-200/90 bg-white p-3.5 shadow-sm transition-all duration-200 hover:border-emerald-500/40 hover:shadow-md active:scale-[0.99]"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-xs font-bold text-slate-900 sm:text-sm group-hover:text-emerald-700 transition-colors">
                            {request.title || request.departmentName || request.category || "Service Request"}
                          </p>
                          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-slate-500">
                            <span>{formatResolutionTime(request)}</span>
                            {request.reminderCount > 0 && (
                              <span className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-1.5 py-0.5 text-[11px] font-semibold text-amber-700 ring-1 ring-amber-200/60">
                                <BellRing className="h-3 w-3" />
                                {request.reminderCount} reminder{request.reminderCount > 1 ? "s" : ""}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="flex shrink-0 items-center gap-2">
                          <RequestStatusBadge status={request.status} />
                          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 text-slate-400 transition-colors group-hover:bg-emerald-50 group-hover:text-emerald-600">
                            <Eye className="h-3.5 w-3.5" />
                          </div>
                        </div>
                      </div>

                      {canPingRequest(request.status) && (
                        <div
                          className="mt-2.5 flex justify-end border-t border-slate-100 pt-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => handlePingRequest(request.id)}
                            disabled={pingingRequestId === request.id}
                            className="h-7 rounded-xl border-emerald-600/30 px-2.5 text-[11px] font-medium text-emerald-700 hover:bg-emerald-50"
                          >
                            {pingingRequestId === request.id ? (
                              <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                            ) : (
                              <BellRing className="mr-1.5 h-3 w-3" />
                            )}
                            Ping staff
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          <section>
            <IOSQuickActionsGrid actions={quickActions} navigate={navigate} />
          </section>

          <section>
            <div className="mb-2.5 flex items-center justify-between">
              <h2 className="text-lg font-extrabold text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.45)] sm:text-xl">
                Recommended Activities
              </h2>

              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/guest/suggestions")}
                className="h-8 px-3 text-xs font-semibold text-white/95 drop-shadow-[0_2px_10px_rgba(0,0,0,0.35)] transition-all duration-200 hover:bg-white/20 hover:text-white"
              >
                View all
              </Button>
            </div>

            {recommendationsLoading ? (
              <div className="flex min-h-[220px] items-center justify-center rounded-3xl border border-white/70 bg-white/90 backdrop-blur-xl">
                <Loader2 className="h-8 w-8 animate-spin text-[#923536]" />
              </div>
            ) : recommendedActivities.length > 0 ? (
              <RecommendedActivitiesSlider
                activities={recommendedActivities}
                onBook={handleBookActivity}
                bookingId={bookingId}
              />
            ) : (
              <div className="rounded-3xl border border-white/70 bg-white/90 p-5 text-center text-xs text-slate-500 backdrop-blur-xl">
                No recommendations available right now.
              </div>
            )}
          </section>

          <section>
            <h2 className="mb-2.5 text-lg font-extrabold text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.45)] sm:text-xl">
              Explore More
            </h2>

            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3 sm:gap-3">
              <GlassActionCard
                icon={MapPin}
                title="Explore Suggestions"
                description="Local attractions & dining"
                gradient="from-emerald-500 to-teal-600"
                onClick={() => navigate("/guest/suggestions")}
              />

              <GlassActionCard
                icon={HelpCircle}
                title="Travel Help"
                description="Emergency, currency & info"
                gradient="from-cyan-500 to-blue-600"
                onClick={() => navigate("/guest/travel-help")}
              />

              <GlassActionCard
                icon={ClipboardCheck}
                title="Feedback"
                description="Share stay experience"
                gradient="from-purple-500 to-indigo-600"
                onClick={() => navigate("/guest/feedback")}
              />
            </div>
          </section>
        </div>
      </GuestLayout>

      <Dialog open={registrationOpen} onOpenChange={setRegistrationOpen}>
        <DialogContent className="max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle>Join rewards</DialogTitle>
            <DialogDescription>
              Register your guest profile so the hotel can recognize your future stays and offers.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <label className="space-y-2">
              <span className="text-sm font-medium">Full name</span>
              <Input
                value={registrationForm.fullName}
                onChange={(event) => setRegistrationForm((prev) => ({ ...prev, fullName: event.target.value }))}
                placeholder={session.guestName || "Your name"}
                className="h-11 w-full rounded-xl border border-border bg-background px-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
            </label>
            <label className="space-y-2">
              <span className="text-sm font-medium">Email address</span>
              <Input
                type="email"
                value={registrationForm.email}
                onChange={(event) => setRegistrationForm((prev) => ({ ...prev, email: event.target.value }))}
                className="h-11 w-full rounded-xl border border-border bg-background px-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
            </label>
            <label className="space-y-2">
              <span className="text-sm font-medium">Phone number</span>
              <Input
                value={registrationForm.phoneNumber}
                onChange={(event) => setRegistrationForm((prev) => ({ ...prev, phoneNumber: event.target.value }))}
                className="h-11 w-full rounded-xl border border-border bg-background px-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
            </label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setRegistrationOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={handleGuestRegistration} disabled={registrationSaving}>
              {registrationSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Register
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GuestHomePage;
